import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";

import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import { SNETTextfield as SNETTextField, alertTypes } from "shared/dist/components";
import validator from "shared/dist/utils/validator";

import { basicDetailsFormData } from "./content";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { organizationActions, loaderActions } from "../../../../../Services/Redux/actionCreators";
import { ContactsTypes } from "../../../../../Utils/Contacts";
import { orgOnboardingConstraints } from "../validationConstraints";
import OrganizationIdAvailability from "./OrganizationIdAvailability";
import { userEntities } from "../../../../../Utils/user";
import { organizationTypes } from "../../../../../Utils/organizationSetup";
import { isEmpty } from "lodash";
import classes from "../Organization.module.css";

let timeout = null;
const TIMEOUT_TINE = 1000;

const BasicDetails = ({ allowDuns, setAllowDuns }) => {
    const dispatch = useDispatch();
    const [validationErrors, setValidationErrors] = useState({
        id: undefined,
        name: undefined,
        website: undefined,
        phone: undefined,
        duns: undefined,
    });
    const { orgDetails, userEntity } = useSelector(
        (state) => ({
            userEntity: state.user.entity,
            orgDetails: state.organization,
        }),
        shallowEqual
    );
    const contact = orgDetails.contacts.find((el) => el.type === ContactsTypes.GENERAL);

    let phone = "";
    if (contact) {
        phone = contact.phone;
    }

    const validateOrgId = async (orgId) => {
        // Call the API to Validate the Org Id
        try {
            const orgAvailability = await dispatch(organizationActions.validateOrgId(orgId));
            dispatch(organizationActions.setOrgAvailability(orgAvailability));
        } catch (error) {
            dispatch(organizationActions.setOrgAvailability(""));
        }
    };

    const handleValidation = (fieldName, value) => {
        const invalidMessage = validator.single(value, orgOnboardingConstraints[fieldName]);
        if (invalidMessage) {
            setValidationErrors({
                ...validationErrors,
                [fieldName]: {
                    type: alertTypes.ERROR,
                    message: invalidMessage[0], // invalidMessage is single-element array
                },
            });
        } else {
            setValidationErrors({ ...validationErrors, [fieldName]: undefined });
        }
        return isEmpty(invalidMessage);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        handleValidation(name, value);
        dispatch(organizationActions.setOneBasicDetail(name, value));
    };

    const handleIdChange = (event) => {
        const { name, value } = event.target;
        dispatch(organizationActions.setOneBasicDetail(name, value.replace(/\s/g, "")));
        clearTimeout(timeout);

        if (!handleValidation(name, value)) {
            dispatch(loaderActions.stopValidateOrgIdLoader());
            dispatch(organizationActions.setOrgAvailability());
            return;
        }
        dispatch(loaderActions.startValidateOrgIdLoader());

        timeout = setTimeout(async () => {
            await validateOrgId(value);
        }, TIMEOUT_TINE);
    };

    const handleContactsChange = (event) => {
        const { name, value } = event.target;
        handleValidation(name, value);
        const updatedContacts = [...orgDetails.contacts];
        const index = orgDetails.contacts.findIndex((el) => el.type === ContactsTypes.GENERAL);
        if (index !== -1) {
            updatedContacts[index] = { ...orgDetails.contacts[index], [name]: value };
        } else {
            updatedContacts[orgDetails.contacts.length] = {
                type: ContactsTypes.GENERAL,
                [name]: value,
            };
        }
        dispatch(organizationActions.setContacts(updatedContacts));
    };

    const Label = ({ isRequired, labelText }) => {
        return (
            <>
                {isRequired && <span className={classes.isRequired}>*</span>}
                <span>{labelText}</span>
            </>
        );
    };

    return (
        <div container className={classes.basicDetailsContainer}>
            {userEntity !== userEntities.INDIVIDUAL &&
                orgDetails.type !== organizationTypes.INDIVIDUAL && (
                    <div>
                        <SNETTextField
                            {...basicDetailsFormData.ORG_ID}
                            label={
                                <Label
                                    isRequired={basicDetailsFormData.ORG_ID.isRequired}
                                    labelText={basicDetailsFormData.ORG_ID.label}
                                />
                            }
                            value={orgDetails.id}
                            onChange={handleIdChange}
                            maxCount={32}
                            inputProps={{ maxLength: 32 }}
                            error={validationErrors[basicDetailsFormData.ORG_ID.name]}
                        />
                        <OrganizationIdAvailability
                            orgDetails={orgDetails}
                            id={orgDetails.id}
                            availability={orgDetails.availability}
                        />
                    </div>
                )}

            <SNETTextField
                {...basicDetailsFormData.ORGANIZATION_NAME}
                label={
                    <Label
                        isRequired={basicDetailsFormData.ORGANIZATION_NAME.isRequired}
                        labelText={basicDetailsFormData.ORGANIZATION_NAME.label}
                    />
                }
                value={orgDetails.name}
                maxCount={50}
                inputProps={{ maxLength: 50 }}
                onChange={handleChange}
                error={validationErrors[basicDetailsFormData.ORGANIZATION_NAME.name]}
            />
            {userEntity !== userEntities.INDIVIDUAL &&
                orgDetails.type !== organizationTypes.INDIVIDUAL && (
                    <div className={classes.dunsContainer}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={allowDuns}
                                    onChange={(e) => setAllowDuns(e.target.checked)}
                                />
                            }
                            label="I have my DUNS number (Optional)"
                        />
                        <SNETTextField
                            {...basicDetailsFormData.DUNS}
                            label={
                                <Label
                                    isRequired={basicDetailsFormData.DUNS.isRequired}
                                    labelText={basicDetailsFormData.DUNS.label}
                                />
                            }
                            value={orgDetails.duns}
                            onChange={handleChange}
                            disabled={!allowDuns}
                            error={
                                allowDuns
                                    ? validationErrors[basicDetailsFormData.DUNS.name]
                                    : undefined
                            }
                        />
                    </div>
                )}
            <SNETTextField
                {...basicDetailsFormData.WEBSITE}
                label={
                    <Label
                        isRequired={basicDetailsFormData.WEBSITE.isRequired}
                        labelText={basicDetailsFormData.WEBSITE.label}
                    />
                }
                value={orgDetails.website}
                error={validationErrors[basicDetailsFormData.WEBSITE.name]}
                onChange={handleChange}
            />
            <SNETTextField
                {...basicDetailsFormData.PHONE}
                label={
                    <Label
                        isRequired={basicDetailsFormData.PHONE.isRequired}
                        labelText={basicDetailsFormData.PHONE.label}
                    />
                }
                value={phone}
                onChange={handleContactsChange}
                error={validationErrors[basicDetailsFormData.PHONE.name]}
            />
        </div>
    );
};

BasicDetails.propTypes = {
    basicDetails: PropTypes.shape({
        name: PropTypes.string,
        duns: PropTypes.string,
        website: PropTypes.string,
        phone: PropTypes.string,
    }),
};

export default BasicDetails;
